import { Button, Group, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { safeApiCall } from "api/common.service";
import { checkCode, submitSupplierCodes } from "api/supplier.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_FORM_INVALID,
  MSG_DEFAULT_FORM_SUBMIT_ERR,
  MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
} from "common/constants";
import React from "react";
import { SupplierProductCode } from "types/model.types";

interface SupplierEditFormProps {
  initial?: SupplierProductCode;
  onSaveComplete: (values: SupplierProductCode) => void;
  onCancel: () => void;
}

const AdminSupplierProductCodeEntryPage: React.FC<SupplierEditFormProps> = ({
  initial,
  onSaveComplete,
  onCancel,
}) => {
  // Define our useForm and must be declared before any UseEffects
  const form = useForm<SupplierProductCode>({
    initialValues: {
      id: initial?.id ?? 0,
      productId: Number(initial?.productId ?? 0),
      supplierId: Number(initial?.supplierId ?? 0),
      alternateCode: initial?.alternateCode ?? "",
      descriptions: initial?.descriptions ?? "",
    },
    validate: {
      productId: (v) => (!v ? "Product is required" : null),
      alternateCode: (v) => (!v ? "Alternate code is required" : null),
      descriptions: (v) => (!v ? "Description is required" : null),
    },
  });
  const editMode = Boolean(initial?.id && Number(initial.id) > 0);

  const handleSubmit = async () => {
    const result = form.validate();
    if (result.hasErrors) {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: MSG_DEFAULT_FORM_INVALID,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      return;
    }
    const payload: SupplierProductCode = form.getValues();
    try {
      if (payload.id === 0) {
        // 1. Pre‑check the product code
        const checkResult = await safeApiCall(checkCode(payload.productId));
        if (!checkResult.exists) {
          notifications.show({
            title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
            message: "Product Id does not exist.",
            position: MantineNotificationPosition.TopCenter,
            color: MantineNotificationColor.Error,
          });
          return; // stop submission
        }
      }

      const result = await safeApiCall(submitSupplierCodes(payload));
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Success,
      });
      onSaveComplete(result);
    } catch {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: MSG_DEFAULT_FORM_SUBMIT_ERR,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
    }
  };

  return (
    <Stack>
      {/* Horizontal row */}
      <Group grow>
        <TextInput
          label="Product Id"
          disabled={editMode}
          onFocus={(e) => e.target.select()}
          placeholder="Product Id"
          {...form.getInputProps("productId")}
        />

        <TextInput
          label="Alternate Code"
          maxLength={50}
          onFocus={(e) => e.target.select()}
          placeholder="Alternate Code"
          {...form.getInputProps("alternateCode")}
        />
      </Group>

      {/* Below the row */}
      <TextInput
        label="Description"
        disabled={editMode}
        onFocus={(e) => e.target.select()}
        maxLength={50}
        placeholder="Description"
        {...form.getInputProps("descriptions")}
      />
      {/* Hidden fields */}
      <input type="hidden" {...form.getInputProps("id")} />
      <input type="hidden" {...form.getInputProps("supplierId")} />

      <Group justify="flex-end" mt="md">
        <Button onClick={handleSubmit}>Save</Button>
        <Button onClick={onCancel}>Cancel</Button>
      </Group>
    </Stack>
  );
};

export default AdminSupplierProductCodeEntryPage;
